import { auth, db } from '@/firebase'
import { addDoc, collection, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query, serverTimestamp, startAfter, Timestamp, where } from 'firebase/firestore'
import { logDebug, logError, logInfo } from '@/utils/secureLogger'

import type { AuditLogEntry, ProfileChangeMetadata, DocumentAccessMetadata, SystemEventMetadata } from '@/interfaces/AuditLog';

export type AuditAction =
  // Tutor Actions
  | 'tutor_application'
  | 'tutor_session_action'
  | 'tutor_profile_update'
  | 'tutor_feedback'
  // Student Actions
  | 'student_session_action'
  | 'student_profile_update'
  | 'account_deletion'
  | 'student_feedback'
  // Admin Actions
  | 'user_management'
  | 'application_decision'
  | 'tutor_applications'
  | 'admin_session_action'
  | 'subject_management'
  | 'document_access'
  | 'admin_auth'
  // System
  | 'profile_update'
  | 'document_download'
  | 'document_view'
  | 'document_upload'
  | 'document_delete'
  | 'system_event'
  | 'other'

export interface AuditLogRecord extends AuditLogEntry {
  id: string;
  createdAt: Date;
  metadata?: ProfileChangeMetadata | DocumentAccessMetadata | SystemEventMetadata;
}

export interface FetchAuditLogsParams {
  action?: AuditAction | 'all';
  type?: AuditLogEntry['type'] | 'all';
  userId?: string | 'all';
  dateFrom?: Date | null;
  dateTo?: Date | null;
  pageSize?: number;
  cursor?: any;
}

const COLLECTION = 'auditLogs'

function toDate(ts: any): Date {
  if (!ts) return new Date(0)
  if (ts instanceof Date) return ts
  if (ts instanceof Timestamp) return ts.toDate()
  return new Date(ts)
}

export async function fetchAuditLogs(params: FetchAuditLogsParams) {
  const {
    action = 'all',
    type = 'all',
    userId = 'all',
    dateFrom = null,
    dateTo = null,
    pageSize = 10,
    cursor = null,
  } = params

  try {
    const constraints: any[] = [orderBy('createdAt', 'desc'), limit(pageSize)]

    if (action !== 'all') constraints.push(where('action', '==', action))
    if (type !== 'all') constraints.push(where('type', '==', type))
    if (userId !== 'all') constraints.push(where('affectedUserId', '==', userId))
    if (dateFrom) constraints.push(where('createdAt', '>=', dateFrom))
    if (dateTo) constraints.push(where('createdAt', '<=', dateTo))
    if (cursor) constraints.push(startAfter(cursor))

    const q = query(collection(db, COLLECTION), ...constraints)
    const snap = await getDocs(q)

    const logs: AuditLogRecord[] = []
    let lastDoc: any = null

    for (const d of snap.docs) {
      const data = d.data() as any
      logs.push({
        id: d.id,
        action: data.action || 'other',
        type: data.type || 'system_event',
        details: data.details || '',
        affectedUserId: data.affectedUserId,
        affectedUserName: data.affectedUserName,
        affectedUserEmail: data.affectedUserEmail || '',
        performedById: data.performedById,
        performedByName: data.performedByName,
        performedByEmail: data.performedByEmail || '',
        timestamp: toDate(data.timestamp || data.createdAt),
        createdAt: toDate(data.createdAt),
        metadata: data.metadata || {}
      })
      lastDoc = d
    }

    logInfo('Fetched audit logs', { count: logs.length })
    return { logs, cursor: lastDoc }
  } catch (error) {
    logError('Failed to fetch audit logs', error)
    throw error
  }
}

export async function resolveUserName(userId: string | undefined): Promise<string | undefined> {
  if (!userId) return undefined
  try {
    const ref = doc(db, 'users', userId)
    const snap = await getDoc(ref)
    if (snap.exists()) {
      const u = snap.data() as any
      return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email
    }
  } catch (error) {
    logDebug('resolveUserName failed', { userId })
  }
  return undefined
}

/**
 * Create an audit log entry
 * Call this whenever an important action happens in your app
 */
export async function createAuditLog(data: {
  action: AuditAction;
  type: AuditLogEntry['type'];
  details: string;
  performedById?: string;
  performedByName?: string;
  performedByEmail?: string;
  affectedUserId?: string;
  affectedUserName?: string;
  affectedUserEmail?: string;
  metadata?: AuditLogEntry['metadata'];
}) {
  try {
    await addDoc(collection(db, COLLECTION), {
      action: data.action,
      type: data.type,
      createdAt: serverTimestamp(),
      timestamp: serverTimestamp(),
      details: data.details,
      performedById: data.performedById || null,
      performedByName: data.performedByName || null,
      performedByEmail: data.performedByEmail || null,
      affectedUserId: data.affectedUserId || null,
      affectedUserName: data.affectedUserName || null,
      affectedUserEmail: data.affectedUserEmail || null,
      metadata: data.metadata || null
    })
    
    logInfo('Audit log created', { action: data.action })
  } catch (error) {
    logError('Failed to create audit log', error)
    // Don't throw - we don't want audit logging to break the main action
  }
}

/**
 * Set up a real-time listener for audit logs
 * Returns an unsubscribe function to stop listening
 */
export async function logProfileChange(
  userId: string,
  field: string,
  oldValue: any,
  newValue: any,
  userType: 'student' | 'tutor'
) {
  const metadata: ProfileChangeMetadata = {
    field,
    oldValue,
    newValue,
    userType
  };

  // Get current user info for performedBy fields
  const currentUser = auth.currentUser;
  let performedByName = 'User';
  let performedByEmail = '';
  
  if (currentUser) {
    // Try to get user's actual name from Firestore
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const firstName = userData.firstName || '';
        const lastName = userData.lastName || '';
        performedByName = `${firstName} ${lastName}`.trim() || currentUser.displayName || currentUser.email || 'User';
      } else {
        performedByName = currentUser.displayName || currentUser.email || 'User';
      }
      performedByEmail = currentUser.email || '';
    } catch (error) {
      logDebug('Could not fetch user name for audit log', { userId: currentUser.uid });
      performedByName = currentUser.displayName || currentUser.email || 'User';
      performedByEmail = currentUser.email || '';
    }
  }

  await createAuditLog({
    action: userType === 'student' ? 'student_profile_update' : 'tutor_profile_update',
    type: 'profile_change',
    details: `Updated ${field} from "${oldValue}" to "${newValue}"`,
    performedById: currentUser?.uid || userId,  // Use current user's ID, not affected user's ID
    performedByName: performedByName,
    performedByEmail: performedByEmail,
    affectedUserId: userId,
    affectedUserName: performedByName,
    affectedUserEmail: performedByEmail,
    metadata
  });
}

export function setupAuditLogsListener(
  params: FetchAuditLogsParams,
  onUpdate: (logs: AuditLogRecord[]) => void
): () => void {
  const {
    action = 'all',
    type = 'all',
    userId = 'all',
    dateFrom = null,
    dateTo = null,
    pageSize = 500,
  } = params

  try {
    logDebug('Setting up audit logs listener', {
      userAuth: {
        uid: auth.currentUser?.uid,
        email: auth.currentUser?.email,
        // Note: Custom claims not directly accessible here
      }
    })
    
    const constraints: any[] = [orderBy('createdAt', 'desc')]

    if (action !== 'all') constraints.push(where('action', '==', action))
    if (type !== 'all') constraints.push(where('type', '==', type))
    if (userId !== 'all') constraints.push(where('affectedUserId', '==', userId))
    if (dateFrom) constraints.push(where('createdAt', '>=', dateFrom))
    if (dateTo) constraints.push(where('createdAt', '<=', dateTo))
    if (pageSize) constraints.push(limit(pageSize))

    const q = query(collection(db, COLLECTION), ...constraints)

    logDebug('Setting up audit logs listener', {
      collection: COLLECTION,
      constraints: constraints.length,
      queryDetails: {
        action,
        type,
        userId,
        dateFrom: dateFrom?.toISOString(),
        dateTo: dateTo?.toISOString(),
        pageSize
      }
    })

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        logDebug('Firestore snapshot received', {
          size: snap.size,
          empty: snap.empty,
          pending: snap.metadata.hasPendingWrites,
          fromCache: snap.metadata.fromCache
        })
        
        const logs: AuditLogRecord[] = []
        
        for (const d of snap.docs) {
          const data = d.data() as any
          logs.push({
            id: d.id,
            action: data.action || 'other',
            type: data.type || 'system_event',
            details: data.details || '',
            affectedUserId: data.affectedUserId,
            affectedUserName: data.affectedUserName,
            affectedUserEmail: data.affectedUserEmail || '',
            performedById: data.performedById,
            performedByName: data.performedByName,
            performedByEmail: data.performedByEmail || '',
            timestamp: toDate(data.timestamp || data.createdAt),
            createdAt: toDate(data.createdAt),
            metadata: data.metadata || {}
          })
        }

        logInfo('Audit logs snapshot processed', { 
          count: logs.length,
          fromCache: snap.metadata.fromCache,
          hasPendingWrites: snap.metadata.hasPendingWrites
        })
        onUpdate(logs)
      },
      (error) => {
        logError('Audit logs listener error', {
          code: error.code,
          message: error.message,
          details: error
        })
      }
    )

    return unsubscribe
  } catch (error) {
    logError('Failed to set up audit logs listener', error)
    return () => {} // Return empty unsubscribe function
  }
}
